import React, { FC, useEffect, useState } from 'react';
import { Button, ToastMessage } from '@cokitchen/cokitchen-components';
import LocalModal from '../../base/local-modal';
import logoutIcon from 'assets/icons/logout-icon.svg';
import { ReactComponent as InfoIcon } from 'assets/icons/info.svg';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import { formatCurrency, handleApiError } from 'utils/functions';
import { EventType } from 'models/event.model';

interface WithdrawFundsModalProps {
  isOpen: boolean;
  onClose: () => void;
  event: EventType;
}

interface WithdrawFundsModalFooterProps {
  onCancel: () => void;
  onConfirm: () => void;
  loading: boolean;
  disabled: boolean;
}

const WithdrawFundsModalFooter: FC<WithdrawFundsModalFooterProps> = ({
  onCancel,
  onConfirm,
  loading,
  disabled
}) => (
  <div className='flex gap-2'>
    <Button small color='primary' variant='outline' className='mx-auto' onClick={onCancel}>
      Cancel
    </Button>
    <Button
      small
      color='primary'
      variant='block'
      className='mx-auto'
      onClick={onConfirm}
      loading={loading}
      disabled={disabled}>
      Confirm
    </Button>
  </div>
);

const WithdrawFundsModal: FC<WithdrawFundsModalProps> = ({ isOpen, onClose, event }) => {
  const [loading, setLoading] = useState(false);
  const [eventWalletLoading, setEventWalletLoading] = useState(false);
  const [eventBalance, setEventBalance] = useState(0);

  const fetchEventWalletBalance = async (id: string) => {
    try {
      setEventWalletLoading(true);
      const res = await apiService.get(`events/wallet/${id}`);
      const eventWalletBalance = Number(res.data.data.available_balance);
      setEventBalance(eventWalletBalance);
    } catch (error) {
      handleApiError(error);
    } finally {
      setEventWalletLoading(false);
    }
  };

  useEffect(() => {
    fetchEventWalletBalance(event.id);
  }, []);

  const confirmWithdraw = async () => {
    setLoading(true);
    try {
      const res = await apiService.post(`events/debit-wallet`, {
        data: {
          event_id: event.id
        }
      });
      if (res.status === 201) {
        toast(<ToastMessage type='success' message={res.data.message} />);
        onClose();
      }
    } catch (err) {
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <LocalModal
      isOpen={isOpen}
      titleIconColor='danger'
      onRequestClose={onClose}
      footer={
        <WithdrawFundsModalFooter
          onCancel={onClose}
          onConfirm={confirmWithdraw}
          loading={loading || eventWalletLoading}
          disabled={eventBalance <= 0}
        />
      }>
      <div className='flex flex-col justify-center items-center'>
        <img src={logoutIcon} className='mx-auto mb-5' />
        <p className='text-[#2E3031] text-center'>
          Are you sure you want to withdraw funds from {event.name}
        </p>
        {eventWalletLoading ? null : (
          <>
            {eventBalance > 0 ? (
              <div className='bg-[#FFF4E5] flex items-start gap-[6px] rounded-[6px] py-2 px-4 mt-5'>
                <InfoIcon />
                <p className='text-[#B26800] text-xs'>
                  Your event balance of {formatCurrency(eventBalance)} will be refunded and added to
                  your business wallet.
                </p>
              </div>
            ) : (
              <div className='bg-[#FFF4E5] flex items-start gap-[6px] rounded-[6px] py-2 px-4 mt-5'>
                <InfoIcon />
                <p className='text-[#B26800] text-xs'>
                  You don’t have any event balance at the moment.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </LocalModal>
  );
};

export default WithdrawFundsModal;
